import { Tooltip, TooltipBoundary, TooltipTrigger } from "@suse-o11y/tooltip";
import React from "react";

import { Star } from "~/components/star";
import { Permission } from "~/models/dto";

import { usePermissions } from "../common/use-permissions";
import { useUserProfile } from "../common/use-user-profile";
import { DashboardFilter } from "../routes/dashboards/dashboard-filter";
import { DASHBOARDS_OVERVIEW_PATH } from "../routes/Routes";

import { LinkTo } from "./react/link";
import { DONT_AUTO_DISMISS, notification } from "./react/notification/Notification";
import { ErrorMessage } from "./_notification-error";

export interface DashboardStarProps {
	id: undefined | number;
	className?: string;
}

export const DashboardStar: React.FC<DashboardStarProps> = ({ id, ...props }) => {
	const permissions = usePermissions();
	const [profile, updateUserProfile] = useUserProfile({
		onError: (error) => {
			notification.error({
				duration: DONT_AUTO_DISMISS,
				message: <ErrorMessage error={error}>Something went wrong while starring a dashboard</ErrorMessage>,
			});
		},
	});

	// starred={undefined} renders a skeleton star
	if (id == null || profile == null || permissions == null) {
		return <Star {...props} starred={undefined} />;
	}

	const hasDeleteFavoriteDashboardsPermission = permissions.hasSystemPermission(Permission.DeleteFavoriteDashboards);
	const hasCreateFavoriteDashboardsPermission = permissions.hasSystemPermission(Permission.CreateFavoriteDashboards);

	const onStarredChange = (next: boolean) => {
		updateUserProfile((current) => ({
			...current,
			starredDashboards: next ? current.starredDashboards.add(id) : current.starredDashboards.delete(id),
		}));
	};

	// starred -> can un-star
	if (profile.starredDashboards.has(id)) {
		return (
			<TooltipBoundary>
				<TooltipTrigger asChild>
					<Star
						{...props}
						onStarredChange={hasDeleteFavoriteDashboardsPermission ? onStarredChange : undefined}
						starred={true}
					/>
				</TooltipTrigger>

				{hasDeleteFavoriteDashboardsPermission ? (
					<Tooltip className="tooltip/tooltip">
						Remove from <LinkToStarredDashboards>starred dashboards</LinkToStarredDashboards>
					</Tooltip>
				) : (
					<Tooltip className="tooltip/tooltip">You don't have permission to un-star this dashboard</Tooltip>
				)}
			</TooltipBoundary>
		);
	}

	// not starred -> can star

	return (
		<TooltipBoundary>
			<TooltipTrigger asChild>
				<Star
					{...props}
					onStarredChange={hasCreateFavoriteDashboardsPermission ? onStarredChange : undefined}
					starred={false}
				/>
			</TooltipTrigger>

			{hasCreateFavoriteDashboardsPermission ? (
				<Tooltip className="tooltip/tooltip">
					Add to <LinkToStarredDashboards>starred dashboards</LinkToStarredDashboards>
				</Tooltip>
			) : (
				<Tooltip className="tooltip/tooltip">You don't have permission to star this dashboard</Tooltip>
			)}
		</TooltipBoundary>
	);
};

const LinkToStarredDashboards: React.FC<React.PropsWithChildren> = ({ children }) => (
	<LinkTo
		className="text-[length:inherit]"
		onClick={(event) => event.stopPropagation()}
		search={({ timeRange, timestamp, detachedFilters }) => ({
			timeRange,
			timestamp,
			detachedFilters,
			dashboardFilter: DashboardFilter.Starred,
		})}
		theme="light"
		// avoid triggering anything else rather than navigation
		to={DASHBOARDS_OVERVIEW_PATH}
	>
		{children}
	</LinkTo>
);
